import {createContext, useContext, useState} from "react";
import {getJsonValue, isBoolTrue} from "../../common/fusion-ui/utils/ReactUtils";
import {AdminKeys} from "../config/AdminConfig";
import useScreenSize from "../../common/fusion-ui/hook/useScreenSize";

const ConfigContext = createContext();
const TAG = 'ConfigProvider';
/**
 * ConfigProvider component that supplies configuration context to its children components.
 * Utilizes the screen size to set initial configuration values.
 * Provides context methods to get, update, and verify configuration values.
 * @param {Object} props Component props.
 * @param {React.ReactNode} props.children The child components to be wrapped by ConfigProvider.
 * @returns {JSX.Element} A provider component with config context value.
 */
const ConfigProvider = ({children}) => {
    const {isScreenUpMd} = useScreenSize();

    const [configData, setConfigData] = useState({
        //default initialization
        [AdminKeys.tracker_SidebarToggleOn]: !isScreenUpMd,
        [AdminKeys.config_navbarCurrPage]: '',
        [AdminKeys.tracker_currPageTag]: '',
    });
    // useEffect(() => {
    // setConfigData(prevState => ({
    //     ...prevState,
    // }));
    // }, []);


    const value = {configData, getConfig, updateConfig, isConfigTrue};
    return <ConfigContext.Provider value={value}>
        {children}
    </ConfigContext.Provider>;

    /**
     * Update a specific configuration value.
     * @param {string} configKey The key to update in the configuration data.
     * @param {any} [value] Value to update config-key with.
     */
    function updateConfig(configKey, value) {
        setConfigData(prevState => ({...prevState, [configKey]: value}));
    }


    /**
     * Retrieves a specific configuration value.
     * @param {string} configKey The key of the configuration value to retrieve.
     * @param {any?} defValue The default value to return if the key is not found.
     * @returns {any} The value associated with the configKey, or the provided defValue.
     */
    function getConfig(configKey, defValue = '') {
        return getJsonValue(configData, configKey, defValue);
    }

    /**
     * Checks if value a config-key is true or false.
     * @param {string} configKey The config-key to check if it's value is true or false.
     * @returns {Boolean} Returns true if value of config-key is true, otherwise false.
     */
    function isConfigTrue(configKey) {
        return isBoolTrue(getConfig(configKey), true, true);
    }
}

/**
 * Custom hook to access & updated the configurable values.
 *
 * For more information, see {@link getConfig} and {@link updateConfig}
 *
 * @returns {{ configData: Object, getConfig(configKey: String, defValue: any): function: any,
 * updateConfig(configKey: String, value: any): function,
 * isConfigTrue(configKey: String): function: boolean }}
 *
 * @example
 * import React from 'react';
 * import { useConfig } from './ConfigProvider';
 *
 * const YourComponent = () => {
 *   const { configData, getConfig, updateConfig, isConfigTrue } = useConfig();
 *
 *   return (
 *     <div>
 *       <button onClick={()=> updateConfig(Keys.config_showHelp, !getConfig(Keys.config_showHelp)) }>
 *           {getConfig(Keys.config_showHelp) ? 'Hide' : 'Show'} Help
 *       </button>
 *     </div>
 *   );
 * };
 *
 * export default YourComponent;
 */
const useConfig = () => {
    return useContext(ConfigContext);
}
export {ConfigProvider, useConfig};
